'use client';

import { AppleIcon, GooglePlayIcon } from '@/components/ui/app-store-badges';
import { Loader2 } from 'lucide-react';

interface OAuthRowProps {
  isAnyLoading: boolean;
  socialLoading: boolean;
  socialError: string | null;
  onGoogleSignIn: () => void;
  onAppleSignIn: () => void;
  /** When provided, renders a Discord login button below the Google/Apple row. Gated by `?discord=true` upstream. */
  onDiscordSignIn?: () => void;
}

const OAUTH_BUTTON =
  'flex h-11 cursor-pointer items-center justify-center gap-2 rounded-[2px] border border-white/10 bg-transparent px-3.5 text-[13px] font-medium text-white transition-colors hover:border-white/25 hover:bg-white/[0.03] disabled:opacity-70';

const DISCORD_BUTTON =
  'mt-2.5 flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-[2px] border border-transparent bg-[#5865F2] px-3.5 text-[13px] font-medium text-white transition-colors hover:bg-[#4752C4] disabled:opacity-70';

function DiscordIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.029ZM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418Zm7.974 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418Z" />
    </svg>
  );
}

export function OAuthRow({
  isAnyLoading,
  socialLoading,
  socialError,
  onGoogleSignIn,
  onAppleSignIn,
  onDiscordSignIn,
}: OAuthRowProps) {
  return (
    <>
      <div className="relative my-4 flex items-center">
        <div className="h-px flex-1 bg-white/[0.08]" />
        <span className="px-2.5 text-[12px] text-text-tertiary tracking-[0.02em]">
          or continue with
        </span>
        <div className="h-px flex-1 bg-white/[0.08]" />
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        <button
          type="button"
          onClick={onGoogleSignIn}
          disabled={isAnyLoading}
          className={OAUTH_BUTTON}
        >
          {socialLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <GooglePlayIcon className="h-4 w-4 text-white" />
          )}
          Google
        </button>

        <button
          type="button"
          onClick={onAppleSignIn}
          disabled={isAnyLoading}
          className={OAUTH_BUTTON}
        >
          {socialLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <AppleIcon className="h-4 w-4 text-white" />
          )}
          Apple
        </button>
      </div>

      {onDiscordSignIn && (
        <button
          type="button"
          onClick={onDiscordSignIn}
          disabled={isAnyLoading}
          className={DISCORD_BUTTON}
        >
          <DiscordIcon className="h-4 w-4" />
          Continue with Discord
        </button>
      )}

      {socialError && <p className="mt-2 text-center text-xs text-red-primary">{socialError}</p>}
    </>
  );
}
